import React from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { useInternalCaseHistory } from '../../../data/support/useSupport';
import {
  CaseHistoryEntry,
  CaseHistoryField,
} from '../../../types/support.types';

const FIELD_LABELS: Record<CaseHistoryField, string> = {
  status: 'changed status',
  priority: 'changed priority',
  assignee: 'changed assignee',
  category: 'changed category',
  subject: 'changed subject',
  sla: 'changed SLA',
  parent_case: 'linked parent case',
  merged_into: 'merged case',
  account: 'changed account',
};

function formatValue(value: any): string {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'object') {
    return (
      value.display_name || value.email || value.id || JSON.stringify(value)
    );
  }
  return String(value);
}

function describeChange(entry: CaseHistoryEntry): string | null {
  if (entry.old_value === null && entry.new_value === null) return null;
  return `${formatValue(entry.old_value)} \u2192 ${formatValue(entry.new_value)}`;
}

interface SupportCaseHistoryProps {
  caseId: string;
}

const SupportCaseHistory: React.FC<SupportCaseHistoryProps> = ({ caseId }) => {
  const historyQuery = useInternalCaseHistory(caseId);
  const entries = historyQuery.data || [];

  if (historyQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!entries.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No history yet</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {entries.map((entry) => {
        const change = describeChange(entry);
        const actorLabel =
          entry.actor?.display_name ||
          entry.actor?.email ||
          (entry.actor_type === 'system' ? 'System' : entry.actor_type);
        return (
          <Box
            key={entry.id}
            sx={{
              display: 'flex',
              gap: 2,
              py: 1.5,
              borderLeft: 2,
              borderColor: 'divider',
              pl: 2,
              ml: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {actorLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {FIELD_LABELS[entry.field] || entry.field}
                </Typography>
              </Box>
              {change && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {change}
                </Typography>
              )}
              {entry.comment && (
                <Typography variant="caption" color="text.secondary">
                  {entry.comment}
                </Typography>
              )}
            </Box>
            <Tooltip
              title={format(parseJSON(entry.created_at), 'PPpp')}
              placement="top"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {formatDistanceToNow(parseJSON(entry.created_at), {
                  addSuffix: true,
                })}
              </Typography>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};

export default SupportCaseHistory;
